import React from 'react';
import { arrayOf, bool, instanceOf, number, object, oneOfType, shape, string } from 'prop-types';
import { LuArrowRight, LuStar } from 'react-icons/lu';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Avatar, NamedLink, OffPlatformReviewAvatar, UserDisplayName } from '../../components';

import css from './SectionReviews.css';

// Translation ids use "ListingSectionReviews": the landing page's own
// SectionReviews component (src/components/SectionReviews) owns "SectionReviews.*".

const SHOWN_REVIEWS = 2;
const STARS = [1, 2, 3, 4, 5];

const newestFirst = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);

const Stars = ({ rating, intl }) => {
  if (!Number.isFinite(rating)) {
    return null;
  }
  const filled = Math.round(rating);
  const label = intl.formatMessage({ id: 'ListingSectionReviews.starsLabel' }, { rating });
  return (
    <span className={css.stars} role="img" aria-label={label}>
      {STARS.map(star => (
        <LuStar
          key={star}
          className={star <= filled ? css.star : css.starEmpty}
          aria-hidden="true"
        />
      ))}
    </span>
  );
};

// DS "Service page / Review card" 317:1934: stars, the quote, then the
// reviewer's avatar, name and month.
const ReviewCard = ({ review, intl }) => {
  const content = typeof review.content === 'string' ? review.content.trim() : '';
  const date = new Date(review.createdAt);
  const month = Number.isNaN(date.getTime())
    ? null
    : intl.formatDate(date, { month: 'long', year: 'numeric' });

  return (
    <li className={css.card}>
      <Stars rating={review.rating} intl={intl} />
      <p className={css.quote}>
        <FormattedMessage id="ListingSectionReviews.quote" values={{ content }} />
      </p>
      <div className={css.reviewer}>
        {review.onPlatform ? (
          <Avatar
            rootClassName={css.avatar}
            user={review.author}
            renderSizes="40px"
            disableProfileLink
            disableVerifiedBadge
          />
        ) : (
          <OffPlatformReviewAvatar
            rootClassName={css.avatar}
            abbreviatedName={review.abbreviatedName}
          />
        )}
        <div className={css.reviewerText}>
          {review.onPlatform ? (
            <UserDisplayName className={css.reviewerName} user={review.author} intl={intl} />
          ) : (
            <span className={css.reviewerName}>{review.authorName}</span>
          )}
          {month ? <span className={css.reviewDate}>{month}</span> : null}
        </div>
      </div>
    </li>
  );
};

// Figma 314:1822 "What clients say": the average and count, the two newest
// reviews (on-platform and off-platform, from summarizeReviews in
// ListingPage.helpers) and a link to all reviews on the author's profile.
// Hidden when there are none.
export const SectionReviewsComponent = props => {
  const { reviews, average, count, authorId, intl } = props;

  const latest = [...(reviews || [])].sort(newestFirst).slice(0, SHOWN_REVIEWS);
  if (!count || latest.length === 0) {
    return null;
  }

  const hasAverage = Number.isFinite(average);

  return (
    <section className={css.root}>
      <div className={css.heading}>
        <div className={css.headingText}>
          <h2 className={css.title}>
            <FormattedMessage id="ListingSectionReviews.title" />
          </h2>
          {hasAverage ? (
            <p className={css.rating}>
              <LuStar className={css.ratingStar} aria-hidden="true" />
              <span className={css.ratingScore}>
                {intl.formatNumber(average, {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}
              </span>
              <span className={css.ratingLabel}>
                <FormattedMessage id="ListingSectionReviews.ratingLabel" values={{ count }} />
              </span>
            </p>
          ) : null}
        </div>
        {authorId ? (
          // The profile shows reviews at the top of its reviews and
          // testimonials card, whose id mirrors ProfileSettingsPage.
          <NamedLink
            className={css.textLink}
            name="ProfilePage"
            params={{ id: authorId }}
            to={{ hash: '#testimonials' }}
          >
            <FormattedMessage id="ListingSectionReviews.allReviews" />
            <LuArrowRight className={css.arrow} aria-hidden="true" />
          </NamedLink>
        ) : null}
      </div>
      <ul className={css.grid}>
        {latest.map((review, index) => (
          <ReviewCard key={review.id || index} review={review} intl={intl} />
        ))}
      </ul>
    </section>
  );
};

SectionReviewsComponent.defaultProps = {
  reviews: [],
  average: null,
  count: 0,
  authorId: null,
};

SectionReviewsComponent.propTypes = {
  reviews: arrayOf(
    shape({
      id: string,
      content: string,
      rating: number,
      createdAt: oneOfType([instanceOf(Date), string]),
      author: object,
      authorName: string,
      abbreviatedName: string,
      onPlatform: bool,
    })
  ),
  average: number,
  count: number,
  authorId: string,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(SectionReviewsComponent);
